import { useEffect } from 'react';

import { Button } from '@/components/Button';
import { KioskCard } from '@/components/KioskCard';
import { KioskScreen } from '@/components/KioskScreen';
import { StatusBadge } from '@/components/StatusBadge';
import { Stepper } from '@/components/Stepper';
import { JOURNEY_STEP } from '@/components/stepper.steps';
import { SuccessBadge } from '@/components/SuccessBadge';
import { useTranslation } from '@/i18n/useTranslation';
import { useKioskSession } from '@/lib/kioskSession';
import { useFocusOnMount } from '@/shared/hooks/useFocusOnMount';

/**
 * The closeout [KAP-2; Figma `11 · That's it for now` — 12302:30396 desktop · 30421 iPad · 30446 mobile].
 *
 * **A screen of its own, and that was the open question.** Decision 0128 offered two shapes for the end of
 * the flow — this separate screen, or the finish screen with *"wait for the representative"* pulled into a
 * block of its own. It was ruled for the block on 2026-09-01 and the ruling was withdrawn the same day.
 * Ruled for THIS on 2026-09-08, on the design file's own evidence: the screen is drawn at all three
 * viewports, numbered into the new-user flow, its heading and body are Heidi Carrel's words from the
 * 2026-09-01 call almost verbatim, and the callout the alternative consisted of was deleted from
 * `10 · Finish` in the same pass.
 *
 * **The rail is complete here, and only here.** `10a · Your next steps` carries none — the rail leaves the
 * screen that used to hold it and arrives, finished, on this one. No story specifies the rail at all;
 * that is still true and still reported.
 *
 * **No QR, no link, no redirect** [decision 0079], and no Back. The one action frees the kiosk for the next
 * person, which is the only thing left to do: the welcome email is already the single way forward
 * [decision 0061].
 */
export function AllDoneScreen() {
  const t = useTranslation();
  const heading = useFocusOnMount<HTMLHeadingElement>();
  const endCandidateSession = useKioskSession((state) => state.endCandidateSession);
  const summonRepresentative = useKioskSession((state) => state.summonRepresentative);

  /*
   * **KPW-2's queue entry — raised where the flow ends and where the promise is made.**
   *
   * `allDone.body` is the promise, in ISS's own words: *"Please have a seat. A representative will call you
   * by name shortly to complete the process."* This screen is reached only from the finish screen's
   * next-steps branch, which exists only when a submission does, so arriving here IS *"a candidate who
   * finished and needs a recruiter"* [KPW-2 § The queue entry says why; reason *ready for recruiter*, 0092].
   *
   * ⚠️ **It used to be raised one screen earlier, on `FinishScreen`'s mount.** 0128 moved the
   * wait-for-a-representative message here and the hand-off stayed behind, so the entry was written for
   * anyone whose session happened to hold a submission — including a candidate who only registered a pay
   * card, who was then queued for a recruiter he had not asked for and who walked away before ever being
   * told one was coming.
   *
   * Idempotent for the whole candidate session, so a candidate already waiting for a representative
   * (an I-9 hand-off, a portal escalation) does not become two rows on one recruiter's queue.
   */
  useEffect(() => {
    summonRepresentative();
  }, [summonRepresentative]);

  return (
    <KioskScreen>
      <KioskCard
        band={<Stepper current={JOURNEY_STEP.allSet} />}
        footer={
          /* Hugged right from `sm` up and full width at 375 — the frame draws a 91px action at 1440 and a
             343px one at 375. */
          <div className="flex w-full flex-col sm:flex-row sm:justify-end">
            <Button onClick={endCandidateSession}>{t('allDone.done')}</Button>
          </div>
        }
        /*
          No Back. Every other screen in the flow offers one; this is the one place where stepping backwards
          means re-entering a finished session, and the frame draws no Back control here either. The badge
          stays, because a candidate looking up needs to know which machine they are standing at.
        */
        header={<StatusBadge label={t('badge.kiosk')} />}
      >
        {/*
          32px under the block, not 40: the frame's `Content` is 192 tall around a 160-tall `intro`
          [12302:30404 / 30405, read live 2026-09-08]. The three children inside it sit 12 apart —
          icon 0–56, heading 68–96, wait message 108–160 — which `gap-3` already gave.
        */}
        <div className="flex w-full flex-col items-center gap-3 pb-8">
          <SuccessBadge />
          <h1
            className="kiosk-heading-focus w-full text-center text-2xl font-bold leading-7 tracking-[-0.36px] text-black"
            ref={heading}
            tabIndex={-1}
          >
            {t('allDone.heading')}
          </h1>
          {/*
            18/26, not 14/24 [12302:30408 / 30433 / 30457, all three widths]. The design sets this above
            body size on purpose: it is the only instruction on the screen, and the candidate reads it
            standing up, about to walk away. Four pixels smaller also changed the line breaks at every
            width.
          */}
          <p className="w-full text-center text-lg leading-[26px] text-[var(--muted-foreground)]">
            {t('allDone.body')}
          </p>
        </div>
      </KioskCard>
    </KioskScreen>
  );
}
